import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import Chart from "chart.js/auto";
import zoomPlugin from "chartjs-plugin-zoom";
import { X, ZoomOut } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { apiGetEcgByUuidOptions } from "../../../api";
import { DefaultFullECGDataSchemaResponse } from "../../../api/generated/types.gen";
import { HelperTooltip } from "../../../components/ui-kit/base/helper-tooltip";
import { Button } from "../../../components/ui-kit/button";
import { parseDatetimeToLocal } from "../../../utils/utils";

// Register zoom plugin
Chart.register(zoomPlugin);

interface ECGPlotProps {
  uuid: string | null;
  onClose: () => void;
}

const colorPalette = [
  "rgba(187, 22, 163, 1)", // Pink/Magenta
  "rgba(5, 195, 222, 1)", // Cyan
  "rgba(0, 146, 203, 1)", // Blue
  "rgba(112, 115, 114, 1)", // Gray
  "rgba(37, 40, 42, 1)", // Dark gray
];

export function ECGPlot(props: ECGPlotProps) {
  const { t } = useLingui();
  // Enables the reset button, which has nothing to undo until the plot is zoomed.
  const [zoomed, setZoomed] = useState(false);
  const chartRef = useRef<Chart | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const query = useQuery({
    ...apiGetEcgByUuidOptions({ path: { uuid: props.uuid ?? "" } }),
    enabled: !!props.uuid,
    meta: { handlesOwnErrors: true },
  });
  const ecgData = props.uuid
    ? ((query.data ?? null) as DefaultFullECGDataSchemaResponse | null)
    : null;
  const loading = query.isLoading;
  const error =
    props.uuid && query.error
      ? query.error instanceof Error
        ? query.error.message
        : t`Failed to load ECG data`
      : null;

  // A new recording gets a new chart, which starts unzoomed.
  useEffect(() => {
    setZoomed(false);
  }, [props.uuid]);

  const resetZoom = () => {
    chartRef.current?.resetZoom();
    setZoomed(false);
  };

  // Create/update chart when data changes
  useEffect(() => {
    // Destroy previous chart
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    if (!ecgData || !canvasRef.current) {
      return;
    }

    if (!ecgData.ecg || !ecgData.leads || !ecgData.sampling_frequency) {
      return;
    }

    // Generate time axis (in seconds)
    const numSamples = ecgData.ecg[0]?.length || 0;
    const timePoints = Array.from(
      { length: numSamples },
      (_, i) => i / ecgData.sampling_frequency,
    );

    // Create datasets for each lead
    const datasets = ecgData.leads.map((lead, index) => ({
      label: lead,
      data: timePoints.map((tp, i) => ({ x: tp, y: ecgData.ecg[index][i] })),
      borderColor: colorPalette[index % colorPalette.length],
      backgroundColor: colorPalette[index % colorPalette.length],
      borderWidth: 1,
      fill: false,
      pointRadius: 0,
      pointHoverRadius: 0,
    }));

    // Chart styling (light mode)
    const textColor = "rgba(17, 24, 39, 1)";
    const gridColor = "rgba(209, 213, 219, 0.5)";
    const backgroundColor = "rgba(255, 255, 255, 1)";

    // Create chart
    const ctx = canvasRef.current.getContext("2d");
    if (ctx) {
      // Add double-click handler to canvas
      canvasRef.current.ondblclick = resetZoom;

      chartRef.current = new Chart(ctx, {
        type: "line",
        data: { datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          backgroundColor: backgroundColor,
          plugins: {
            legend: {
              display: true,
              position: "top",
              labels: {
                usePointStyle: true,
                color: textColor,
              },
            },
            zoom: {
              zoom: {
                drag: {
                  enabled: true,
                },
                mode: "x",
                onZoomComplete: ({ chart }) =>
                  setZoomed(chart.isZoomedOrPanned()),
              },
              pan: {
                enabled: true,
                mode: "x",
                modifierKey: "ctrl",
                onPanComplete: ({ chart }) =>
                  setZoomed(chart.isZoomedOrPanned()),
              },
              limits: {
                x: { min: "original", max: "original" },
                y: { min: "original", max: "original" },
              },
            },
            tooltip: {
              mode: "index",
              intersect: false,
              position: "nearest",
            },
          },
          scales: {
            x: {
              type: "linear",
              title: {
                display: true,
                text: t`time [s]`,
                font: {
                  size: 14,
                  weight: "bold",
                },
                color: textColor,
              },
              ticks: {
                color: textColor,
              },
              grid: {
                color: gridColor,
              },
            },
            y: {
              title: {
                display: true,
                text: t`amplitude [mV]`,
                font: {
                  size: 14,
                  weight: "bold",
                },
                color: textColor,
              },
              ticks: {
                color: textColor,
              },
              grid: {
                color: gridColor,
              },
            },
          },
        },
      });
    }

    // Cleanup on unmount
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [ecgData]);

  if (!props.uuid) {
    return null;
  }

  return (
    <div className="mb-6 rounded-lg border border-input bg-background p-4">
      <div className="mb-1 flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">
            <Trans>ECG Plot</Trans>
            {ecgData?.measurement_datetime && (
              <>
                {" - "}
                {parseDatetimeToLocal(ecgData.measurement_datetime)}
              </>
            )}
          </h3>
        </div>
        <div className="flex items-center gap-3">
          <HelperTooltip
            content={
              <Trans>
                Click and drag to zoom, Ctrl+drag to pan, double-click to reset
              </Trans>
            }
          />
          <Button
            variant="tertiary"
            icon={ZoomOut}
            label={t`Reset zoom`}
            disabled={!zoomed}
            onClick={resetZoom}
          />
          <Button
            variant="tertiary"
            icon={X}
            label={t`Close ECG plot`}
            tooltip={null}
            onClick={props.onClose}
          />
        </div>
      </div>

      {loading && (
        <div className="flex h-96 items-center justify-center">
          <div className="text-muted-foreground">
            <Trans>Loading ECG data...</Trans>
          </div>
        </div>
      )}

      {error && (
        <div className="flex h-96 items-center justify-center">
          <div className="text-danger-foreground">{error}</div>
        </div>
      )}

      {ecgData && !loading && !error && (
        <div className="h-96">
          <canvas ref={canvasRef} />
        </div>
      )}
    </div>
  );
}
